import React, { useState, FormEvent } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import DescriptionIcon from '@mui/icons-material/Description'
import EditIcon from '@mui/icons-material/Edit'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FolderIcon from '@mui/icons-material/Folder'
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks'
import MenuIcon from '@mui/icons-material/Menu'
import SettingsIcon from '@mui/icons-material/Settings'
import { Link, router, useForm } from '@inertiajs/react'
import * as yup from 'yup'
import { AppLayout } from '@/components/layouts/AppLayout'
import { Markdown } from '@/components/Markdown'

interface AgentRef {
  id: number
  username: string
  slug: string
}

interface NoteItem {
  id: number
  title: string
  content: string | null
  folder_id: number | null
  folder_name: string | null
  created_by: AgentRef
  updated_by: AgentRef
  created_at: string
  updated_at: string
}

interface FolderItem {
  id: number
  name: string
  position: number
  note_count: number
}

interface FolderOption {
  id: number
  name: string
}

interface NotebookDetail {
  id: number
  name: string
  slug: string
  description: string | null
  is_admin: boolean
  can_edit: boolean
}

interface Props {
  notebook: NotebookDetail
  notes: NoteItem[]
  folders: FolderItem[]
  view?: string
  selected_note_id?: number
  selected_folder_id?: number
  edit_note?: { id: number; title: string; content: string | null; folder_id: number | null }
  folder_options?: FolderOption[]
}

type Selection =
  | { type: 'root' }
  | { type: 'folder'; id: number }
  | { type: 'note'; id: number }
  | { type: 'new_note' }
  | { type: 'edit_note'; id: number }

function initSelection(props: Props): Selection {
  if (props.view === 'note' && props.selected_note_id) {
    return { type: 'note', id: props.selected_note_id }
  }
  if (props.view === 'new_note') {
    return { type: 'new_note' }
  }
  if (props.view === 'edit_note' && props.selected_note_id) {
    return { type: 'edit_note', id: props.selected_note_id }
  }
  if ((props.view === 'folder' || props.selected_folder_id) && props.selected_folder_id) {
    return { type: 'folder', id: props.selected_folder_id }
  }
  return { type: 'root' }
}

function buildUrl(slug: string, selection: Selection): string {
  const base = `/notebooks/notebooks/${slug}`
  switch (selection.type) {
    case 'root': return base
    case 'folder': return `${base}?folder_id=${selection.id}`
    case 'note': return `${base}/notes/${selection.id}`
    case 'new_note': return `${base}/notes/new`
    case 'edit_note': return `${base}/notes/${selection.id}/edit`
  }
}

// --- Sub-components ---

function NewFolderForm({ notebookSlug }: { notebookSlug: string }) {
  const { data, setData, post, processing, reset } = useForm({
    folder: { name: '' }
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!data.folder.name.trim()) return
    post(`/notebooks/notebooks/${notebookSlug}/folders`, {
      onSuccess: () => reset(),
    })
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 1, p: 1 }}>
      <TextField
        size="small"
        placeholder="New folder..."
        value={data.folder.name}
        onChange={e => setData('folder', { name: e.target.value })}
        sx={{ flex: 1 }}
      />
      <Button type="submit" size="small" variant="outlined" disabled={processing}>
        Add
      </Button>
    </Box>
  )
}

function NoteCard({ note, onClick }: { note: NoteItem; onClick: () => void }) {
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <DescriptionIcon sx={{ color: 'text.secondary' }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={500}>
          {note.title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Updated {new Date(note.updated_at).toLocaleDateString()} by {note.updated_by.username}
          {note.folder_name && ` in ${note.folder_name}`}
        </Typography>
      </Box>
    </Paper>
  )
}

function NoteListPanel({
  title,
  notes,
  notebook,
  onSelectNote,
  onNewNote,
}: {
  title: string
  notes: NoteItem[]
  notebook: NotebookDetail
  onSelectNote: (id: number) => void
  onNewNote: () => void
}) {
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h2">{title}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {notebook.can_edit && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={onNewNote}
            >
              New note
            </Button>
          )}
          {notebook.is_admin && (
            <Button
              component={Link}
              href={`/notebooks/notebooks/${notebook.slug}/settings`}
              variant="outlined"
              size="small"
              startIcon={<SettingsIcon />}
            >
              Settings
            </Button>
          )}
        </Box>
      </Box>
      {notebook.description && title === notebook.name && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {notebook.description}
        </Typography>
      )}
      {notes.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>
          No notes in this view.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {notes.map((note) => (
            <NoteCard key={note.id} note={note} onClick={() => onSelectNote(note.id)} />
          ))}
        </Box>
      )}
    </>
  )
}

function NoteDetailPanel({
  note,
  notebook,
  canEdit,
  onEdit,
}: {
  note: NoteItem
  notebook: NotebookDetail
  canEdit: boolean
  onEdit: () => void
}) {
  function handleDelete() {
    if (confirm(`Delete note '${note.title}'? This cannot be undone.`)) {
      router.delete(`/notebooks/notebooks/${notebook.slug}/notes/${note.id}`)
    }
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h2" sx={{ mb: 1 }}>{note.title}</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {note.folder_name && (
              <Chip label={note.folder_name} size="small" variant="outlined" />
            )}
            <Typography variant="caption" color="text.secondary">
              Created by {note.created_by.username} on {new Date(note.created_at).toLocaleDateString()}
            </Typography>
            {note.updated_by.id !== note.created_by.id && (
              <Typography variant="caption" color="text.secondary">
                · Updated by {note.updated_by.username}
              </Typography>
            )}
          </Box>
        </Box>
        {canEdit && (
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={onEdit}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Box>
        )}
      </Box>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 4 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        {note.content ? (
          <Markdown>{note.content}</Markdown>
        ) : (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            This note has no content yet.
          </Typography>
        )}
      </Paper>
    </>
  )
}

const noteSchema = yup.object({
  note: yup.object({
    title: yup.string().required('Title is required').max(255, 'Title must be at most 255 characters'),
  })
})

function NoteFormPanel({
  notebook,
  folders,
  editNote,
  onCancel,
}: {
  notebook: NotebookDetail
  folders: FolderOption[]
  editNote?: { id: number; title: string; content: string | null; folder_id: number | null }
  onCancel: () => void
}) {
  const isEdit = !!editNote
  const { data, setData, post, put, processing, errors, setError, clearErrors } = useForm({
    note: {
      title: editNote?.title || '',
      content: editNote?.content || '',
      folder_id: (editNote?.folder_id || '') as number | '',
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    clearErrors()

    try {
      noteSchema.validateSync(data, { abortEarly: false })
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const validationErrors: Record<string, string> = {}
        err.inner.forEach((e) => {
          if (e.path) validationErrors[e.path] = e.message
        })
        setError(validationErrors)
        return
      }
    }

    if (isEdit) {
      put(`/notebooks/notebooks/${notebook.slug}/notes/${editNote.id}`)
    } else {
      post(`/notebooks/notebooks/${notebook.slug}/notes`)
    }
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h2">{isEdit ? `Edit: ${editNote.title}` : 'New note'}</Typography>
        <Button size="small" onClick={onCancel}>Cancel</Button>
      </Box>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Title"
            fullWidth
            autoFocus
            value={data.note.title}
            onChange={e => setData('note', { ...data.note, title: e.target.value })}
            error={'note.title' in errors}
            helperText={(errors as Record<string, string>)['note.title']}
            sx={{ mb: 3 }}
          />
          {folders.length > 0 && (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Folder (optional)</InputLabel>
              <Select
                value={data.note.folder_id}
                label="Folder (optional)"
                onChange={e => setData('note', { ...data.note, folder_id: e.target.value as number | '' })}
              >
                <MenuItem value="">No folder</MenuItem>
                {folders.map((f) => (
                  <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            label="Content (Markdown)"
            fullWidth
            multiline
            rows={16}
            value={data.note.content}
            onChange={e => setData('note', { ...data.note, content: e.target.value })}
            sx={{ mb: 3 }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={processing}
          >
            {isEdit ? 'Update note' : 'Create note'}
          </Button>
        </Box>
      </Paper>
    </>
  )
}

// --- Main component ---

export default function Show(props: Props) {
  const { notebook, notes, folders, edit_note, folder_options } = props
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [explorerOpen, setExplorerOpen] = useState(false)

  const [selection, setSelection] = useState<Selection>(() => initSelection(props))
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(() => {
    const initial = new Set(folders.map(f => f.id))
    // Also expand the folder containing the selected note
    const init = initSelection(props)
    if (init.type === 'note' || init.type === 'edit_note') {
      const note = notes.find(n => n.id === init.id)
      if (note?.folder_id) initial.add(note.folder_id)
    }
    return initial
  })

  const unfiledNotes = notes.filter(n => n.folder_id === null)

  function navigate(sel: Selection) {
    setSelection(sel)
    window.history.pushState({}, '', buildUrl(notebook.slug, sel))
    if (isMobile) setExplorerOpen(false)
  }

  function toggleFolder(folderId: number) {
    setExpandedFolders(prev => {
      const next = new Set(prev)
      if (next.has(folderId)) {
        next.delete(folderId)
      } else {
        next.add(folderId)
      }
      return next
    })
  }

  function handleSelectFolder(folderId: number) {
    navigate({ type: 'folder', id: folderId })
    setExpandedFolders(prev => {
      const next = new Set(prev)
      next.add(folderId)
      return next
    })
  }

  function handleDeleteFolder(folder: FolderItem) {
    if (confirm(`Delete folder '${folder.name}'? Notes in this folder will be moved to the root.`)) {
      router.delete(`/notebooks/notebooks/${notebook.slug}/folders/${folder.id}`, {
        onSuccess: () => {
          if (selection.type === 'folder' && selection.id === folder.id) {
            navigate({ type: 'root' })
          }
        }
      })
    }
  }

  function getFilteredNotes(): NoteItem[] {
    if (selection.type === 'root') return notes
    if (selection.type === 'folder') return notes.filter(n => n.folder_id === selection.id)
    return []
  }

  function getContentTitle(): string {
    if (selection.type === 'root') return notebook.name
    if (selection.type === 'folder') {
      const folder = folders.find(f => f.id === selection.id)
      return folder?.name || ''
    }
    return ''
  }

  const selectedNote = (selection.type === 'note')
    ? notes.find(n => n.id === selection.id)
    : null

  // Folder options for forms: use folder_options from props if available, otherwise derive from folders
  const formFolderOptions: FolderOption[] = folder_options || folders.map(f => ({ id: f.id, name: f.name }))

  function renderContent() {
    if (selection.type === 'new_note') {
      return (
        <NoteFormPanel
          notebook={notebook}
          folders={formFolderOptions}
          onCancel={() => navigate({ type: 'root' })}
        />
      )
    }

    if (selection.type === 'edit_note') {
      const noteToEdit = edit_note || (() => {
        const n = notes.find(n => n.id === selection.id)
        return n ? { id: n.id, title: n.title, content: n.content, folder_id: n.folder_id } : undefined
      })()
      if (noteToEdit) {
        return (
          <NoteFormPanel
            notebook={notebook}
            folders={formFolderOptions}
            editNote={noteToEdit}
            onCancel={() => navigate({ type: 'note', id: noteToEdit.id })}
          />
        )
      }
    }

    if (selection.type === 'note' && selectedNote) {
      return (
        <NoteDetailPanel
          note={selectedNote}
          notebook={notebook}
          canEdit={notebook.can_edit}
          onEdit={() => navigate({ type: 'edit_note', id: selectedNote.id })}
        />
      )
    }

    return (
      <NoteListPanel
        title={getContentTitle()}
        notes={getFilteredNotes()}
        notebook={notebook}
        onSelectNote={(id) => navigate({ type: 'note', id })}
        onNewNote={() => navigate({ type: 'new_note' })}
      />
    )
  }

  const explorerContent = (
    <Box
      sx={{
        width: isMobile ? 280 : undefined,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight={600} noWrap>
              {notebook.name}
            </Typography>
            {notebook.description && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {notebook.description}
              </Typography>
            )}
          </Box>

          <List dense disablePadding sx={{ flex: 1, overflow: 'auto', py: 1 }}>
            {/* All Notes */}
            <ListItemButton
              selected={selection.type === 'root'}
              onClick={() => navigate({ type: 'root' })}
              sx={{ borderRadius: 1, mx: 0.5, px: 1.5 }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <LibraryBooksIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="All Notes" />
              <Chip label={notes.length} size="small" variant="outlined" />
            </ListItemButton>

            <Divider sx={{ my: 1 }} />

            {/* Folders with nested notes */}
            {folders.map((folder) => {
              const folderNotes = notes.filter(n => n.folder_id === folder.id)
              const isExpanded = expandedFolders.has(folder.id)
              const isSelected = selection.type === 'folder' && selection.id === folder.id

              return (
                <React.Fragment key={folder.id}>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => handleSelectFolder(folder.id)}
                    sx={{ borderRadius: 1, mx: 0.5, px: 1.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <FolderIcon fontSize="small" color={isSelected ? 'primary' : 'inherit'} />
                    </ListItemIcon>
                    <ListItemText primary={folder.name} />
                    <Chip label={folderNotes.length} size="small" variant="outlined" sx={{ mr: 0.5 }} />
                    {notebook.can_edit && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteFolder(folder)
                        }}
                        sx={{ p: 0.25, opacity: 0.5, '&:hover': { opacity: 1 } }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleFolder(folder.id)
                      }}
                      sx={{ p: 0.25 }}
                    >
                      {isExpanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </ListItemButton>
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List dense disablePadding>
                      {folderNotes.map((note) => (
                        <ListItemButton
                          key={note.id}
                          selected={
                            (selection.type === 'note' || selection.type === 'edit_note') &&
                            selection.id === note.id
                          }
                          onClick={() => navigate({ type: 'note', id: note.id })}
                          sx={{ borderRadius: 1, mx: 0.5, pl: 5.5 }}
                        >
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <DescriptionIcon sx={{ fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={note.title}
                            primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                          />
                        </ListItemButton>
                      ))}
                      {folderNotes.length === 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ pl: 6.5, py: 0.5, display: 'block' }}>
                          Empty folder
                        </Typography>
                      )}
                    </List>
                  </Collapse>
                </React.Fragment>
              )
            })}

            {/* Unfiled notes */}
            {unfiledNotes.length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.5, display: 'block' }}>
                  Unfiled
                </Typography>
                {unfiledNotes.map((note) => (
                  <ListItemButton
                    key={note.id}
                    selected={
                      (selection.type === 'note' || selection.type === 'edit_note') &&
                      selection.id === note.id
                    }
                    onClick={() => navigate({ type: 'note', id: note.id })}
                    sx={{ borderRadius: 1, mx: 0.5, px: 1.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <DescriptionIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={note.title}
                      primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                    />
                  </ListItemButton>
                ))}
              </>
            )}
          </List>

          {/* New folder form */}
          {notebook.can_edit && (
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
              <NewFolderForm notebookSlug={notebook.slug} />
            </Box>
          )}
    </Box>
  )

  return (
    <AppLayout maxWidth={false} noPadding>
      <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 64px)',
        }}
      >
        {/* Desktop explorer */}
        {!isMobile && (
          <Box
            sx={{
              width: { md: 300, lg: '30%' },
              maxWidth: 400,
              flexShrink: 0,
              borderRight: '1px solid',
              borderColor: 'divider',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {explorerContent}
          </Box>
        )}

        {/* Mobile explorer drawer */}
        {isMobile && (
          <Drawer
            anchor="left"
            open={explorerOpen}
            onClose={() => setExplorerOpen(false)}
            PaperProps={{ sx: { width: 280 } }}
          >
            {explorerContent}
          </Drawer>
        )}

        {/* Content panel */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: { xs: 2, md: 3 },
          }}
        >
          {isMobile && (
            <IconButton
              onClick={() => setExplorerOpen(true)}
              sx={{ mb: 1, minWidth: 44, minHeight: 44 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          {renderContent()}
        </Box>
      </Box>
    </AppLayout>
  )
}
